import { useState } from 'react';
import Logo from '../../components/base/Logo';

export default function TermsOfService() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <a href="/" className="cursor-pointer">
                <Logo />
              </a>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a href="/" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">Home</a>
                <a href="/#services" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">Services</a>
                <a href="/#about" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">About</a>
                <a href="/#contact" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">Contact</a>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <a href="tel:9514234096" className="flex items-center space-x-2 text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <span className="font-semibold">(951) 423-4096</span>
              </a>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-gray-900 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-menu-line text-xl"></i>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-100">
              <a href="/" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">Home</a>
              <a href="/#services" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">Services</a>
              <a href="/#about" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">About</a>
              <a href="/#contact" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Terms of Service Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> January 2026
          </p>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 mb-4">
              By engaging Apex Home Inspection Services, LLC ("Company," "we," "our," or "us") for home inspection services, you ("Client," "you," or "your") agree to be bound by these Terms of Service. Please read these terms carefully before scheduling an inspection.
            </p>
            <p className="text-gray-600 mb-4">
              If you do not agree to these terms, please do not use our services. Your continued use of our services constitutes acceptance of these terms and any future modifications.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Services Provided</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Scope of Inspection</h3>
            <p className="text-gray-600 mb-4">
              Our home inspection services include a visual examination of the readily accessible areas of the property. We inspect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Structural components (foundation, framing, roof structure)</li>
              <li>Exterior (siding, doors, windows, trim)</li>
              <li>Roofing system</li>
              <li>Plumbing system</li>
              <li>Electrical system</li>
              <li>Heating and cooling systems</li>
              <li>Interior components (walls, ceilings, floors, doors, windows)</li>
              <li>Insulation and ventilation</li>
              <li>Built-in appliances</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Limitations</h3>
            <p className="text-gray-600 mb-4">
              Our inspection is visual and non-invasive. We do not:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Move furniture, stored items, or personal belongings</li>
              <li>Dismantle equipment or remove covers (except electrical panels)</li>
              <li>Inspect areas that are not readily accessible</li>
              <li>Perform engineering, architectural, or specialized inspections</li>
              <li>Inspect for environmental hazards (mold, asbestos, radon, lead paint)</li>
              <li>Inspect for wood-destroying organisms or pests</li>
              <li>Determine property boundary lines or encroachments</li>
              <li>Inspect swimming pools, spas, or outbuildings (unless specifically contracted)</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Standards of Practice</h2>
            <p className="text-gray-600 mb-4">
              Our inspections are conducted in accordance with the Standards of Practice established by:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>California Real Estate Inspection Association (CREIA)</li>
              <li>International Association of Certified Home Inspectors (InterNACHI)</li>
              <li>California Business and Professions Code</li>
            </ul>
            <p className="text-gray-600 mb-4">
              These standards define the minimum requirements for a home inspection and are available for your review upon request.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Scheduling and Payment</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Scheduling</h3>
            <p className="text-gray-600 mb-4">
              Inspections are scheduled on a first-come, first-served basis. We make every effort to accommodate your preferred date and time. Same-day inspections may be available based on inspector availability.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Payment Terms</h3>
            <p className="text-gray-600 mb-4">
              Payment is due at the time of inspection unless other arrangements have been made in advance. We accept:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Credit and debit cards</li>
              <li>Cash</li>
              <li>Check (with prior approval)</li>
              <li>Electronic payment methods</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Cancellation Policy</h3>
            <p className="text-gray-600 mb-4">
              Cancellations must be made at least 24 hours before the scheduled inspection time. Cancellations made with less than 24 hours notice may be subject to a cancellation fee of up to 50% of the inspection cost.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Client Responsibilities</h2>
            <p className="text-gray-600 mb-4">
              To ensure a thorough and efficient inspection, the Client agrees to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Provide access to all areas of the property</li>
              <li>Ensure utilities (water, gas, electricity) are turned on</li>
              <li>Clear access to electrical panels, water heaters, HVAC equipment, and attic/crawl space access points</li>
              <li>Inform the inspector of any known defects or concerns</li>
              <li>Provide keys, access codes, or gate remotes as needed</li>
              <li>Ensure the property is safe for inspection</li>
            </ul>
            <p className="text-gray-600 mb-4">
              If access is restricted or utilities are not operational, we reserve the right to reschedule or charge an additional fee for a return visit.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Inspection Report</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Report Delivery</h3>
            <p className="text-gray-600 mb-4">
              A comprehensive written report will be delivered electronically within 24-48 hours of the inspection. The report includes:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Description of inspected systems and components</li>
              <li>Identification of defects and safety concerns</li>
              <li>Photographic documentation</li>
              <li>Recommendations for repairs or further evaluation</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Report Usage</h3>
            <p className="text-gray-600 mb-4">
              The inspection report is prepared exclusively for the Client and may not be transferred, assigned, or relied upon by any other party without our written consent. The report is not a warranty, guarantee, or insurance policy.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">General Limitations</h3>
            <p className="text-gray-600 mb-4">
              Our inspection is a visual examination of the property's condition at the time of inspection. We cannot and do not guarantee that all defects will be discovered. Some defects may be concealed, latent, or not readily apparent during a visual inspection.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Liability Cap</h3>
            <p className="text-gray-600 mb-4">
              Our liability for errors, omissions, or negligence is limited to the amount paid for the inspection service, or $500, whichever is greater. This limitation applies regardless of the cause of action, whether in contract, tort, negligence, or otherwise.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Exclusions</h3>
            <p className="text-gray-600 mb-4">
              We are not liable for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Conditions that were concealed, latent, or not readily accessible</li>
              <li>Defects that develop after the inspection</li>
              <li>Failure to comply with manufacturer specifications or building codes</li>
              <li>Cosmetic defects or aesthetic concerns</li>
              <li>Future performance of systems or components</li>
              <li>Consequential or indirect damages</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Dispute Resolution</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Notification</h3>
            <p className="text-gray-600 mb-4">
              If you believe we have made an error or omission, you must notify us in writing within 14 days of receiving the inspection report. Failure to provide timely notice waives any claims.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Mediation and Arbitration</h3>
            <p className="text-gray-600 mb-4">
              Any disputes arising from these Terms of Service or the inspection shall first be submitted to mediation. If mediation is unsuccessful, the dispute shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Governing Law</h3>
            <p className="text-gray-600 mb-4">
              These Terms of Service are governed by the laws of the State of California. Any legal proceedings shall be conducted in the appropriate courts of California.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Warranties and Disclaimers</h2>
            <p className="text-gray-600 mb-4">
              We provide our services with reasonable care and skill. However, we make no warranties, express or implied, regarding:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>The condition, value, or insurability of the property</li>
              <li>The remaining useful life of systems or components</li>
              <li>The presence or absence of environmental hazards</li>
              <li>Compliance with building codes or regulations</li>
              <li>The suitability of the property for any particular purpose</li>
            </ul>
            <p className="text-gray-600 mb-4">
              Our inspection is not a substitute for specialized inspections by qualified professionals (structural engineers, pest control operators, environmental specialists, etc.).
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Recommendations</h2>
            <p className="text-gray-600 mb-4">
              When we recommend further evaluation or repair by a qualified professional, we strongly advise you to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Obtain estimates for recommended repairs before closing</li>
              <li>Have all recommended evaluations completed by licensed specialists</li>
              <li>Consider the cost and priority of repairs in your purchase decision</li>
              <li>Understand that some issues may require immediate attention for safety reasons</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Confidentiality</h2>
            <p className="text-gray-600 mb-4">
              We maintain the confidentiality of your inspection and report. We will not disclose information about the property or inspection findings to third parties without your written consent, except:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>As required by law or court order</li>
              <li>To our insurance carrier in the event of a claim</li>
              <li>To professional organizations for quality assurance purposes (with identifying information removed)</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Insurance and Licensing</h2>
            <p className="text-gray-600 mb-4">
              Apex Home Inspection Services, LLC maintains:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Professional liability insurance (errors and omissions)</li>
              <li>General liability insurance</li>
              <li>Current California home inspector licenses</li>
              <li>Membership in professional inspection associations</li>
            </ul>
            <p className="text-gray-600 mb-4">
              Proof of insurance and licensing is available upon request.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Modifications to Terms</h2>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the modified terms.
            </p>
            <p className="text-gray-600 mb-4">
              We recommend reviewing these terms periodically to stay informed of any updates.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Severability</h2>
            <p className="text-gray-600 mb-4">
              If any provision of these Terms of Service is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Entire Agreement</h2>
            <p className="text-gray-600 mb-4">
              These Terms of Service, together with our Privacy Policy and any written inspection agreement, constitute the entire agreement between you and Apex Home Inspection Services, LLC. These terms supersede all prior agreements, understandings, and communications, whether written or oral.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Contact Information</h2>
            <p className="text-gray-600 mb-4">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mt-4">
              <p className="text-gray-700 mb-2"><strong>Apex Home Inspection Services, LLC</strong></p>
              <p className="text-gray-600 mb-2">Phone: (951) 423-4096</p>
              <p className="text-gray-600 mb-2">Email: info@apexhomeinspectionservices.com</p>
              <p className="text-gray-600">Service Area: Southern California</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Acknowledgment</h2>
            <p className="text-gray-600 mb-4">
              By scheduling and proceeding with an inspection, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. You also acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>The inspection is visual and non-invasive</li>
              <li>Not all defects can be discovered during a visual inspection</li>
              <li>You should obtain specialized inspections as recommended</li>
              <li>The inspection report is not a warranty or guarantee</li>
              <li>Our liability is limited as described in these terms</li>
            </ul>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="mb-4">
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center transform rotate-6">
                      <i className="ri-file-list-3-line text-white text-xl"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs font-bold"></i>
                    </div>
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-lg font-bold text-white">Apex</span>
                    <span className="text-xs font-semibold text-teal-400 -mt-1">HOME INSPECTION</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Southern California's trusted home inspection service. Licensed, certified, and committed to protecting your investment.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/#services" className="hover:text-white transition-colors cursor-pointer">Pre-Purchase Inspection</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors cursor-pointer">Pre-Listing Inspection</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors cursor-pointer">Maintenance Inspection</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors cursor-pointer">Specialty Inspections</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>(951) 423-4096</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line"></i>
                  <span>info@apexhomeinspectionservices.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Southern California</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2026 Apex Home Inspection Services, LLC. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
              <a href="/terms-of-service" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
